import { useGetRecoveryStatusQuery } from 'api/recoveryAi'
import ProtectedRoute from 'components/ProtectedRoute'
import Spinner from 'components/Spinner'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import useFlagsReady from 'hooks/useFlagsReady'
import { buildTranslate } from 'locales'
import React from 'react'
import { useSelector } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router-dom'
import { selectCurrentMerchantUser } from 'selectors/merchantUser'
import ActivationSuccess from './Activation/ActivationSuccess'
import ConfiguredSummary from './Activation/ConfiguredSummary'
import CaseDetailOrInboxRedirect from './CaseDetailOrInboxRedirect'
import EscalationDetail from './EscalationDetail'
import Landing from './Landing'
import { ACTIVATION_PATH, ACTIVATION_SUCCESS_PATH } from './routes'
import Configure from './Wizard'
import { WizardProvider } from './Wizard/WizardContext'

const t = buildTranslate('recovery_ai')

const RecoveryAI = () => {
  const recoveryAiEnabled = useFlag('recovery-ai', false)
  // `useFlag` is `useFlags()[key] ?? default`, so it cannot tell "flag is off"
  // apart from "LaunchDarkly has not evaluated this context yet" — both read
  // false. On a hard refresh the second case is the real one: FlagsProvider
  // boots the LD client against the `merchant-logged-out` context and only
  // re-keys to `merchant-<id>` (via ldClient.identify) once BOTH the merchant
  // and the MerchantUser ORM entities have landed. This route mounts before
  // that, because selectUserIsAuthenticated gates on the auth-state user id
  // rather than the entity — so redirecting on a bare `!recoveryAiEnabled`
  // bounced any refresh of /recovery_ai back to the app root (the section was
  // reachable only by in-app navigation, after identify had long since run).
  //
  // Hold on a spinner until LD has actually evaluated the merchant's context.
  // Waiting on the entities alone is NOT enough: identify() is only *called*
  // once they land, so an entities-only guard releases at the exact moment the
  // round trip begins and every flag still reads false. useFlagsReady is
  // published by FlagsProvider when identify resolves (or fails), which is the
  // signal that actually closes the window. This is the same shape as
  // ProtectedRoute's own isDataLoading guard — see the "refreshing the page
  // will cause the user to ALWAYS get redirected out of the route" comment
  // there — extended to cover the flag evaluation as well as the data.
  const merchant = useCurrentMerchant()
  const merchantUser = useSelector(selectCurrentMerchantUser)
  const areFlagsReady = useFlagsReady()
  const isLdContextPending = !merchant || !merchantUser || !areFlagsReady
  // The same ungated entitlement probe the nav item reads — same cache entry, so
  // this is not a second request. Called before the flag early-return so the hook
  // order stays stable; `skip` keeps it off the wire for a flag-off merchant.
  const { data: status, isLoading: isStatusLoading } =
    useGetRecoveryStatusQuery(undefined, { skip: !recoveryAiEnabled })

  if (isLdContextPending) {
    return (
      <div role="status" aria-live="polite" aria-label={t('loading')}>
        <Spinner isLoading size="4x" className="text-center margin-top-huge" />
      </div>
    )
  }

  if (!recoveryAiEnabled) {
    return <Redirect to="/" />
  }

  return (
    <Switch>
      {/* WizardProvider is mounted ABOVE the per-:step route content so the
          wizard's in-memory state survives step-to-step navigation. (The whole
          RecoveryAI subtree formerly remounted on every navigation because its
          route used an inline-built component; that root cause is fixed in
          Routes.tsx by pinning RecoveryAI's component identity.) Scoping the
          provider to /configure — rather than wrapping the whole Switch — means
          it unmounts when the merchant leaves the wizard, so re-entering
          re-hydrates from the latest saved config (picking up edits made in the
          Settings panes), and the config query doesn't run on the non-wizard
          recovery pages. */}
      <Route
        path="/recovery_ai/configure/:step"
        render={() => (
          <WizardProvider>
            <ProtectedRoute
              redirect="/"
              resource="MerchantSettings"
              accessType="manage"
              path="/recovery_ai/configure/:step"
              component={Configure}
            />
          </WizardProvider>
        )}
      />
      <ProtectedRoute
        redirect="/"
        resource="MerchantSettings"
        path="/recovery_ai/cases/:id"
        component={CaseDetailOrInboxRedirect}
      />
      <ProtectedRoute
        redirect="/"
        resource="MerchantSettings"
        path="/recovery_ai/escalations/:id"
        component={EscalationDetail}
      />
      {/* WP7 (AI-39): the activation surfaces sit ABOVE the Landing catch-all
          (non-exact, so it would otherwise swallow them). The more-specific
          /activation/success MUST precede /activation. The configured-summary
          admits any MerchantSettings viewer (accessType="access") so a non-admin reaches
          the preparer-handoff fork (U11).
          The success page is "access" too, and must stay that way: it is reached
          ONLY by redirect from the activate PUT, so anyone landing here has already
          cleared useIsActivationAdmin — a strictly stronger check. Gating it on
          manage:MerchantSettings instead re-derives authority from a different source
          (/me permissions) than the gate that admitted them (rbac_role_ids), and
          any disagreement between the two charges the merchant and then redirects
          them to the app root with no receipt. Keyed to the weaker check, that
          class of bug is unrepresentable. */}
      <ProtectedRoute
        redirect="/"
        resource="MerchantSettings"
        accessType="access"
        path={ACTIVATION_SUCCESS_PATH}
        component={ActivationSuccess}
      />
      <ProtectedRoute
        redirect="/"
        resource="MerchantSettings"
        accessType="access"
        path={ACTIVATION_PATH}
        component={ConfiguredSummary}
      />
      {/* The Landing catch-all is the ONE conditionally-gated route here: for a
          merchant who has not set RecoveryAI up it is pure marketing copy and
          must be reachable by any authenticated merchant user, while for a
          set-up merchant it is the live console and keeps today's
          MerchantSettings gate. ProtectedRoute takes a STATIC resource and has
          no conditional form (it is used app-wide — not ours to change), so the
          choice is made here, inside a `render` wrapper: Switch reads `path` off
          its direct children, so the wrapper — not the conditional itself — has
          to carry it. Mirrors the /configure/:step route above. */}
      <Route
        path="/recovery_ai"
        render={() => {
          // Decide only once the probe has answered. Rendering the ungated Route
          // first would flash a set-up merchant's console open for a user who is
          // then redirected out of it — the ProtectedRoute isDataLoading spinner
          // exists for exactly this reason. `isLoading` covers the first load
          // only and is false for an errored or skipped query, so this can never
          // spin forever; an unavailable probe falls through to the open Route,
          // where Landing's own MerchantSettings check still serves the promo
          // page to a user who can't see the console.
          if (isStatusLoading) {
            return (
              <Spinner
                isLoading
                size="4x"
                className="text-center margin-top-huge"
              />
            )
          }

          return status?.configured ? (
            <ProtectedRoute
              redirect="/"
              resource="MerchantSettings"
              path="/recovery_ai"
              component={Landing}
            />
          ) : (
            <Route path="/recovery_ai" component={Landing} />
          )
        }}
      />
    </Switch>
  )
}

export default RecoveryAI
